import ExcelJS from "exceljs";

const toCellText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof value === "object") {
    if (Array.isArray(value?.richText)) {
      return value.richText.map((x) => String(x?.text || "")).join("").trim();
    }
    if (value?.text != null) return String(value.text).trim();
    if (value?.result != null) return String(value.result).trim();
  }
  return String(value || "").trim();
};

const toNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "object" && value?.result != null) return toNumber(value.result);
  const normalized = String(value).trim().replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};
const MAX_ODO_DELTA_PER_DAY = 2000;
const buildKoreksiOdoHariIniNotice = (nopol) => (
  `ODO yang telah dikoreksi hari ini tidak dapat diupdate melalui menu bulk, kosongkan/skip input odo untuk MT ${String(nopol || "-").trim() || "-"}`
);
const normalizeJenisTransaksi = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (["normal", "odo_rusak", "mt_off"].includes(normalized)) return normalized;
  if (normalized === "koreksi_odo") return "koreksi_odo";
  return "";
};
const normalizeDateKey = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const getDaySpanAtLeastOne = (fromDate, toDate) => {
  const fromKey = normalizeDateKey(fromDate);
  const toKey = normalizeDateKey(toDate);
  if (!fromKey || !toKey) return 1;
  const fromObj = new Date(`${fromKey}T00:00:00`);
  const toObj = new Date(`${toKey}T00:00:00`);
  const diffMs = toObj.getTime() - fromObj.getTime();
  if (!Number.isFinite(diffMs)) return 1;
  const diffDays = Math.floor(diffMs / 86400000);
  return Math.max(1, diffDays);
};

export async function uploadBulkOdo(fileData) {
  if (!fileData) {
    return {
      valid: [],
      invalid: [],
      totalReadRows: 0,
      kmHeader: "",
      headerDate: "",
      headerError: "File belum dipilih.",
    };
  }

  const workbook = new ExcelJS.Workbook();
  const buffer = await fileData.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0];
  if (!worksheet) {
    return {
      valid: [],
      invalid: [],
      totalReadRows: 0,
      kmHeader: "",
      headerDate: "",
      headerError: "Sheet tidak ditemukan.",
    };
  }

  const headerRow = worksheet.getRow(1);
  const headersRaw = headerRow.values.slice(1).map((x) => toCellText(x));
  const headersNormalized = headersRaw.map((x) => x.toLowerCase());

  const idxIdMt = headersNormalized.indexOf("id_mt");
  const idxNopol = headersNormalized.indexOf("nopol");
  const idxLastOdo = headersNormalized.indexOf("last odo");
  const idxLastOdoDate = headersNormalized.indexOf("tanggal update odo");
  const idxKmInput = headersNormalized.findIndex((x) => x.startsWith("km odo tgl."));
  const idxJenisTransaksi = headersNormalized.indexOf("jenis transaksi");
  const idxNamaDriver = headersNormalized.indexOf("nama driver");
  const idxCatatanFlag = headersNormalized.indexOf("catatan") >= 0
    ? headersNormalized.indexOf("catatan")
    : headersNormalized.indexOf("catatan flag");
  const idxKeterangan = headersNormalized.indexOf("keterangan");

  if (idxIdMt < 0 || idxNopol < 0 || idxLastOdo < 0 || idxLastOdoDate < 0 || idxKmInput < 0) {
    return {
      valid: [],
      invalid: [],
      totalReadRows: 0,
      kmHeader: "",
      headerDate: "",
      headerError: "Header file tidak sesuai format formulir bulk ODO.",
    };
  }

  const kmHeader = headersRaw[idxKmInput] || "";
  const headerDateMatch = kmHeader.match(/km\s*odo\s*tgl\.\s*(\d{4}-\d{2}-\d{2})/i);
  const headerDate = String(headerDateMatch?.[1] || "").trim();
  if (!headerDate) {
    return {
      valid: [],
      invalid: [],
      totalReadRows: 0,
      kmHeader,
      headerDate: "",
      headerError: "Header kolom ODO harus berformat: km odo tgl. YYYY-MM-DD",
    };
  }
  const valid = [];
  const invalid = [];
  const seenValidMt = new Map();

  const lastRow = Math.max(worksheet.rowCount, 1);
  for (let rowNumber = 2; rowNumber <= lastRow; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    const idMt = toCellText(row.getCell(idxIdMt + 1).value);
    const nopol = toCellText(row.getCell(idxNopol + 1).value);
    const lastOdoNum = toNumber(row.getCell(idxLastOdo + 1).value);
    const lastOdoDate = toCellText(row.getCell(idxLastOdoDate + 1).value);
    const odoInputRaw = row.getCell(idxKmInput + 1).value;
    const odoInputNum = toNumber(odoInputRaw);
    const odoInputText = toCellText(odoInputRaw);
    const jenisTransaksiRaw = idxJenisTransaksi >= 0
      ? toCellText(row.getCell(idxJenisTransaksi + 1).value)
      : "";
    const jenisTransaksi = normalizeJenisTransaksi(jenisTransaksiRaw);
    const namaDriver = idxNamaDriver >= 0
      ? toCellText(row.getCell(idxNamaDriver + 1).value)
      : "";
    const catatanFlag = idxCatatanFlag >= 0
      ? toCellText(row.getCell(idxCatatanFlag + 1).value)
      : "";
    const keterangan = idxKeterangan >= 0
      ? toCellText(row.getCell(idxKeterangan + 1).value)
      : "";

    const isEmptyLine = !idMt && !nopol && !odoInputText;
    if (isEmptyLine) continue;

    if (!odoInputText) continue;
    if (keterangan.includes("telah dikoreksi hari ini")) {
      invalid.push({ rowNumber, id_mt: idMt, nopol, reason: buildKoreksiOdoHariIniNotice(nopol) });
      continue;
    }
    if (!idMt) {
      invalid.push({ rowNumber, id_mt: "", nopol, reason: "ID MT kosong." });
      continue;
    }
    if (!Number.isFinite(lastOdoNum)) {
      invalid.push({ rowNumber, id_mt: idMt, nopol, reason: "Last ODO tidak valid." });
      continue;
    }
    if (!Number.isFinite(odoInputNum)) {
      invalid.push({ rowNumber, id_mt: idMt, nopol, reason: "Input ODO bukan angka." });
      continue;
    }
    if (jenisTransaksiRaw && !jenisTransaksi) {
      invalid.push({ rowNumber, id_mt: idMt, nopol, reason: "Jenis transaksi harus normal, odo_rusak, atau mt_off." });
      continue;
    }
    if (jenisTransaksi === "koreksi_odo") {
      invalid.push({ rowNumber, id_mt: idMt, nopol, reason: "Koreksi ODO hanya bisa dilakukan secara satuan." });
      continue;
    }
    if (odoInputNum < lastOdoNum) {
      invalid.push({ rowNumber, id_mt: idMt, nopol, reason: "Input ODO lebih kecil dari Last ODO." });
      continue;
    }
    const spanDays = getDaySpanAtLeastOne(lastOdoDate, headerDate);
    const maxDelta = spanDays * MAX_ODO_DELTA_PER_DAY;
    const delta = Number(odoInputNum) - Number(lastOdoNum);
    if (delta > maxDelta) {
      invalid.push({
        rowNumber,
        id_mt: idMt,
        nopol,
        reason: `Selisih ODO melebihi batas ${MAX_ODO_DELTA_PER_DAY} km/hari (maks ${maxDelta} km untuk ${spanDays} hari).`,
      });
      continue;
    }
    if (seenValidMt.has(idMt)) {
      invalid.push({ rowNumber, id_mt: idMt, nopol, reason: `Duplikat ID MT (sudah di baris ${seenValidMt.get(idMt)}).` });
      continue;
    }

    seenValidMt.set(idMt, rowNumber);
    valid.push({
      rowNumber,
      id_mt: idMt,
      nopol,
      last_odo: Number(lastOdoNum),
      last_odo_date: normalizeDateKey(lastOdoDate),
      odo_input: Number(odoInputNum),
      jenis_transaksi: jenisTransaksi,
      nama_driver: namaDriver,
      catatan_flag: catatanFlag,
    });
  }

  return {
    valid,
    invalid,
    totalReadRows: valid.length + invalid.length,
    kmHeader,
    headerDate,
    headerError: "",
  };
}
